import { Fragment, useEffect, useMemo, useState, type ComponentType } from "react";
import {
  ArrowRight,
  Bolt,
  BriefcaseBusiness,
  Code2,
  Heart,
  Languages,
  LockKeyhole,
  Moon,
  Play,
  ShieldCheck,
  Sun,
  UsersRound
} from "lucide-react";
import { AccountNavigation, AuthProvider } from "@aicofi/sdk/react";
import { copy, features, monetizationCards, navItems, personas, proofs, type Locale } from "./data";
import {
  LOCALE_STORAGE_KEY,
  resolveLocale,
  resolveTheme,
  THEME_STORAGE_KEY,
  toHtmlLang,
  type Theme
} from "./lib/prefs";
type IconComponent = ComponentType<{ size?: number; strokeWidth?: number; [key: string]: unknown }>;

const appUrl = import.meta.env.VITE_APP_URL ?? "https://iearn.aicofi.com";

const featureImages: Record<(typeof features)[number]["icon"], string> = {
  wand: "/portal-assets/feature-create.png",
  send: "/portal-assets/feature-publish.png",
  coin: "/portal-assets/feature-monetize.png",
  chart: "/portal-assets/feature-measure.png"
};

const personaIcons: Record<(typeof personas)[number]["icon"], IconComponent> = {
  creators: UsersRound,
  brands: BriefcaseBusiness,
  operators: OperatorIcon,
  developers: Code2
};

const personaImages: Record<(typeof personas)[number]["icon"], string> = {
  creators: "/portal-assets/avatar-creator.png",
  brands: "/portal-assets/avatar-brand.png",
  operators: "/portal-assets/avatar-operator.png",
  developers: "/portal-assets/agent-robot.png"
};

const proofIcons: Record<(typeof proofs)[number]["icon"], IconComponent> = {
  shield: ShieldCheck,
  bolt: Bolt,
  heart: Heart,
  lock: LockKeyhole
};

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage can be unavailable (private mode, blocked cookies); preferences are best-effort.
  }
}

function getInitialLocale(): Locale {
  if (typeof window === "undefined") return "en";
  return resolveLocale(readStorage(LOCALE_STORAGE_KEY), window.navigator.language);
}

function getInitialTheme(): Theme {
  if (typeof window === "undefined") return "light";
  return resolveTheme(readStorage(THEME_STORAGE_KEY));
}

function setMeta(selector: string, content: string) {
  document.querySelector(selector)?.setAttribute("content", content);
}

function OperatorIcon({ size = 24, "aria-hidden": ariaHidden = true }: { size?: number; "aria-hidden"?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 28 28" aria-hidden={ariaHidden} fill="none">
      <path d="M5 23h18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      <path d="M7 11.5h14v11H7v-11Z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M14 5l9 6.5H5L14 5Z" fill="currentColor" opacity="0.18" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M11 15v4M17 15v4" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function Logo({ locale }: { locale: Locale }) {
  return (
    <a className="brand" href="#top" aria-label={locale === "zh" ? "iEarn 首页" : "iEarn home"}>
      <span className="brand-mark" aria-hidden="true">
        <img src="/portal-assets/iearn-logo-icon.png" alt="" />
      </span>
      <span className="brand-word">iEarn</span>
    </a>
  );
}

function Header({
  locale,
  theme,
  onToggleLocale,
  onToggleTheme
}: {
  locale: Locale;
  theme: Theme;
  onToggleLocale: () => void;
  onToggleTheme: () => void;
}) {
  const t = copy[locale];

  return (
    <header className="site-header">
      <Logo locale={locale} />
      <nav aria-label={locale === "zh" ? "主导航" : "Primary navigation"}>
        {navItems.map((item) => (
          <a href={`#${item.id}`} key={item.id}>
            {item.label[locale]}
          </a>
        ))}
      </nav>
      <div className="header-actions">
        <button
          className="language-button"
          type="button"
          onClick={onToggleLocale}
          aria-label={t.switchLanguage}
          lang={locale === "zh" ? "en" : "zh-CN"}
        >
          <Languages size={16} aria-hidden="true" />
          <span>{t.language}</span>
        </button>
        <button
          className="theme-button"
          type="button"
          onClick={onToggleTheme}
          aria-label={theme === "dark" ? t.switchToLight : t.switchToDark}
          aria-pressed={theme === "dark"}
        >
          <span className={theme === "light" ? "active" : ""}>
            <Sun size={17} aria-hidden="true" />
          </span>
          <span className={theme === "dark" ? "active" : ""}>
            <Moon size={17} aria-hidden="true" />
          </span>
        </button>
        <div className="header-account">
          <AccountNavigation profileHref="https://accounts.aicofi.com/profile" />
        </div>
        <a className="header-cta" href={appUrl}>
          {t.launch}
          <ArrowRight size={17} aria-hidden="true" />
        </a>
      </div>
    </header>
  );
}

function SocialOrbit() {
  const socials = [
    ["chip-ig", "/portal-assets/social-instagram.png"],
    ["chip-yt", "/portal-assets/social-youtube.png"],
    ["chip-tt", "/portal-assets/social-tiktok.png"],
    ["chip-x", "/portal-assets/social-x.png"],
    ["chip-heart", "/portal-assets/social-heart.png"],
    ["chip-coin", "/portal-assets/social-coin.png"]
  ] as const;

  return (
    <div className="orbit-stage" aria-hidden="true">
      <span className="sparkle sparkle-a">✦</span>
      <span className="sparkle sparkle-b">✦</span>
      <span className="sparkle sparkle-c">✦</span>
      <span className="orbit-line orbit-one" />
      <span className="orbit-line orbit-two" />
      {socials.map(([className, src]) => (
        <span className={`social-chip ${className}`} key={className}>
          <img src={src} alt="" />
        </span>
      ))}
      <div className="hero-mascot">
        <img src="/portal-assets/iearn-mascot.png?v=20260610-mascot2" alt="" />
      </div>
    </div>
  );
}

function CalendarCard({ locale }: { locale: Locale }) {
  const t = copy[locale];
  const thumbnails = [
    "/portal-assets/calendar-post.png",
    "/portal-assets/calendar-short.png",
    "/portal-assets/calendar-live.png",
    "/portal-assets/calendar-reel.png",
    "/portal-assets/calendar-palm.png"
  ];

  return (
    <section className="content-calendar" aria-label={t.calendarTitle}>
      <h2>{t.calendarTitle}</h2>
      <div className="calendar-days">
        {t.calendarDays.map((day, index) => (
          <span className={index === 2 ? "selected" : ""} key={day}>
            {day}
          </span>
        ))}
      </div>
      <div className="calendar-posts">
        {t.calendarCards.map((card, index) => (
          <article key={`${card}-${index}`}>
            <span>{card}</span>
            <div className={`post-art post-art-${index + 1}`}>
              <img src={thumbnails[index]} alt="" />
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function HeroSideCards({ locale }: { locale: Locale }) {
  const t = copy[locale];

  return (
    <div className="hero-side">
      <CalendarCard locale={locale} />
      <div className="side-card-row">
        <article className="earnings-card">
          <span>{t.earningsTitle}</span>
          <strong>$12,840.75</strong>
          <small>{t.earningsGrowth}</small>
          <i aria-hidden="true" />
        </article>
        <article className="bounty-card">
          <span>{t.bountyTitle}</span>
          <strong>{t.bountyName}</strong>
          <p>
            {t.bountyReward} <b>$320</b>
          </p>
          <ArrowRight size={16} aria-hidden="true" />
        </article>
      </div>
    </div>
  );
}

function Hero({ locale }: { locale: Locale }) {
  const t = copy[locale];

  return (
    <section className="hero-shell" id="top">
      <div className="hero-copy">
        <h1>
          <span>{t.heroLine1}</span>
          <span>{t.heroLine2}</span>
          <span className="accent">{t.heroLine3}</span>
        </h1>
        <p>{t.heroBody}</p>
        <div className="hero-actions">
          <a className="primary-button" href={appUrl}>
            {t.launch}
            <ArrowRight size={19} aria-hidden="true" />
          </a>
          <a className="watch-link" href="#platform">
            {t.seeHow}
            <span>
              <Play size={15} fill="currentColor" aria-hidden="true" />
            </span>
          </a>
        </div>
      </div>
      <SocialOrbit />
      <HeroSideCards locale={locale} />
    </section>
  );
}

function FeatureStrip({ locale }: { locale: Locale }) {
  return (
    <section className="feature-strip" id="platform" aria-label={copy[locale].featureLabel}>
      {features.map((feature) => {
        return (
          <article className={`feature-item feature-${feature.icon}`} key={feature.title.en}>
            <div>
              <img src={featureImages[feature.icon]} alt="" />
            </div>
            <span>
              <strong>{feature.title[locale]}</strong>
              <small>{feature.text[locale]}</small>
            </span>
          </article>
        );
      })}
    </section>
  );
}

function MonetizationSection({ locale }: { locale: Locale }) {
  const t = copy[locale];

  return (
    <section className="monetization-section" id="brands">
      <div className="monetization-copy">
        <img src="/portal-assets/feature-monetize.png" alt="" />
        <h2>{t.monetizationTitle}</h2>
        <p>{t.monetizationBody}</p>
      </div>
      <div className="monetization-grid">
        {monetizationCards.map((card) => (
          <article key={card.title.en}>
            <span>{card.label[locale]}</span>
            <strong>{card.value}</strong>
            <h3>{card.title[locale]}</h3>
            <p>{card.text[locale]}</p>
          </article>
        ))}
      </div>
      <div className="monetization-flow" aria-hidden="true">
        {(t.flowLabels as string[]).map((label, index) => (
          <Fragment key={label}>
            {index > 0 && <i />}
            <span>{label}</span>
          </Fragment>
        ))}
      </div>
    </section>
  );
}

function PersonaSection({ locale }: { locale: Locale }) {
  return (
    <section className="audience-section" id="creators">
      <div className="audience-panel">
        <h2>{copy[locale].universeTitle}</h2>
        <div className="persona-grid">
          {personas.map((persona) => {
            const Icon = personaIcons[persona.icon];
            return (
              <article className={`persona-card persona-${persona.icon}`} key={persona.title.en}>
                <div className="avatar-scene" aria-hidden="true">
                  <img src={personaImages[persona.icon]} alt="" />
                </div>
                <div className="persona-content">
                  <Icon size={25} aria-hidden="true" />
                  <h3>{persona.title[locale]}</h3>
                  <p>{persona.text[locale]}</p>
                  <ul className="persona-highlights" aria-label={locale === "zh" ? "角色能力" : "Role capabilities"}>
                    {persona.highlights[locale].map((highlight) => (
                      <li key={highlight}>{highlight}</li>
                    ))}
                  </ul>
                </div>
                <a href={appUrl} aria-label={`${persona.title[locale]} ${copy[locale].launch}`}>
                  <ArrowRight size={19} aria-hidden="true" />
                </a>
              </article>
            );
          })}
        </div>
      </div>
      <DeveloperCard locale={locale} />
    </section>
  );
}

function DeveloperCard({ locale }: { locale: Locale }) {
  const t = copy[locale];

  return (
    <section className="developer-card" id="developers">
      <div className="developer-head">
        <div>
          <h2>{t.developerTitle}</h2>
          <p>{t.developerBody}</p>
        </div>
        <RobotIllustration />
      </div>
      <a className="entrance-card" href={appUrl}>
        <span>{t.productEntrance}</span>
        <strong>{appUrl.replace("https://", "")}</strong>
        <small>{t.entranceBody}</small>
        <i>
          <ArrowRight size={21} aria-hidden="true" />
        </i>
      </a>
    </section>
  );
}

function RobotIllustration() {
  return (
    <div className="robot-illustration" aria-hidden="true">
      <span className="code-badge">
        <Code2 size={20} />
      </span>
      <span className="chat-badge" />
      <img src="/portal-assets/agent-robot.png" alt="" />
    </div>
  );
}

function ProofBar({ locale }: { locale: Locale }) {
  return (
    <section className="proof-bar">
      {proofs.map((proof) => {
        const Icon = proofIcons[proof.icon];
        return (
          <article key={proof.title.en}>
            <span className={`proof-icon proof-${proof.icon}`}>
              <Icon size={20} aria-hidden="true" />
            </span>
            <span>
              <strong>{proof.title[locale]}</strong>
              <small>{proof.text[locale]}</small>
            </span>
          </article>
        );
      })}
    </section>
  );
}

function Footer({ locale }: { locale: Locale }) {
  const t = copy[locale];

  return (
    <footer className="site-footer">
      <div className="site-footer-row">
        <div className="site-footer-logos">
          <Logo locale={locale} />
          <a
            className="site-footer-home"
            href="https://aicofi.com"
            aria-label={t.aicofiHome}
            target="_blank"
            rel="noopener"
          >
            <img
              className="site-footer-logo site-footer-logo-light"
              src="/aicofi-wave-web-light.png"
              alt="AICOFI"
              width={104}
              height={26}
            />
            <img
              className="site-footer-logo site-footer-logo-dark"
              src="/aicofi-wave-web-dark.png"
              alt="AICOFI"
              width={104}
              height={26}
            />
          </a>
        </div>
        <nav className="site-footer-links" aria-label={t.footerLegal}>
          <a href="https://aicofi.com/privacy" target="_blank" rel="noopener">
            {t.footerPrivacy}
          </a>
          <a href="https://aicofi.com/terms" target="_blank" rel="noopener">
            {t.footerTerms}
          </a>
          <a href="https://aicofi.com/contact" target="_blank" rel="noopener">
            {t.footerContact}
          </a>
        </nav>
      </div>
      <div className="site-footer-row">
        <span className="site-footer-copy">{t.footerCopyright}</span>
        <nav className="site-footer-social" aria-label={t.footerSocial}>
          <a href="https://aicofi.com/contact#social" aria-label="X" target="_blank" rel="noopener">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                d="M14.7 10.6 22.1 2h-1.8l-6.4 7.4L8.8 2H2.9l7.8 11.3L2.9 22h1.8l6.8-7.8 5.4 7.8h5.9l-8.1-11.4Zm-2.4 2.8-.8-1.1L5.2 3.3H8l5 7.1.8 1.1 6.6 9.4h-2.8l-5.3-7.5Z"
                fill="currentColor"
              />
            </svg>
          </a>
          <a href="https://aicofi.com/contact#social" aria-label="LinkedIn" target="_blank" rel="noopener">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9.6h4v11.9H3V9.6Zm6.4 0h3.8v1.6h.1c.5-.9 1.8-1.9 3.7-1.9 4 0 4.7 2.6 4.7 6v6.2h-4v-5.5c0-1.3 0-3-1.8-3s-2.1 1.4-2.1 2.9v5.6h-4V9.6Z"
                fill="currentColor"
              />
            </svg>
          </a>
          <a href="https://aicofi.com/contact#social" aria-label="YouTube" target="_blank" rel="noopener">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                d="M23 7.4a3 3 0 0 0-2.1-2.1C19 4.8 12 4.8 12 4.8s-7 0-8.9.5A3 3 0 0 0 1 7.4 31.4 31.4 0 0 0 .5 12c0 1.6.2 3.2.5 4.6a3 3 0 0 0 2.1 2.1c1.9.5 8.9.5 8.9.5s7 0 8.9-.5a3 3 0 0 0 2.1-2.1c.3-1.4.5-3 .5-4.6s-.2-3.2-.5-4.6ZM9.8 15.4V8.6l5.9 3.4-5.9 3.4Z"
                fill="currentColor"
              />
            </svg>
          </a>
          <a href="https://aicofi.com/contact#social" aria-label="GitHub" target="_blank" rel="noopener">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                d="M12 1.8A10.2 10.2 0 0 0 8.8 21.7c.5.1.7-.2.7-.5v-1.8c-2.9.6-3.5-1.2-3.5-1.2-.5-1.1-1.1-1.4-1.1-1.4-.9-.6.1-.6.1-.6 1 .1 1.6 1.1 1.6 1.1.9 1.6 2.4 1.1 3 .9.1-.7.4-1.1.7-1.4-2.3-.3-4.7-1.1-4.7-5 0-1.1.4-2 1.1-2.8-.1-.3-.5-1.3.1-2.8 0 0 .9-.3 2.9 1.1a10 10 0 0 1 5.3 0c2-1.4 2.9-1.1 2.9-1.1.6 1.5.2 2.5.1 2.8.7.8 1.1 1.7 1.1 2.8 0 3.9-2.4 4.7-4.7 5 .4.3.7 1 .7 2v2.4c0 .3.2.6.7.5A10.2 10.2 0 0 0 12 1.8Z"
                fill="currentColor"
              />
            </svg>
          </a>
        </nav>
      </div>
    </footer>
  );
}

export function App() {
  const [locale, setLocale] = useState<Locale>(getInitialLocale);
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const t = useMemo(() => copy[locale], [locale]);

  useEffect(() => {
    const htmlLang = toHtmlLang(locale);
    document.documentElement.lang = htmlLang;
    document.title = t.metaTitle;
    setMeta('meta[name="description"]', t.metaDescription);
    setMeta('meta[property="og:title"]', t.metaTitle);
    setMeta('meta[property="og:description"]', t.metaDescription);
    setMeta('meta[property="og:locale"]', htmlLang === "zh-CN" ? "zh_CN" : "en_US");
    setMeta('meta[name="twitter:title"]', t.metaTitle);
    setMeta('meta[name="twitter:description"]', t.metaDescription);
    writeStorage(LOCALE_STORAGE_KEY, locale);
  }, [locale, t.metaDescription, t.metaTitle]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    setMeta('meta[name="theme-color"]', theme === "dark" ? "#080b22" : "#f6fbff");
    writeStorage(THEME_STORAGE_KEY, theme);
  }, [theme]);

  return (
    <AuthProvider>
      <div className="portal-frame">
        <a className="skip-link" href="#main-content">
          {t.skipToContent}
        </a>
        <Header
          locale={locale}
          theme={theme}
          onToggleLocale={() => setLocale((current) => (current === "zh" ? "en" : "zh"))}
          onToggleTheme={() => setTheme((current) => (current === "dark" ? "light" : "dark"))}
        />
        <main id="main-content" tabIndex={-1}>
          <Hero locale={locale} />
          <FeatureStrip locale={locale} />
          <PersonaSection locale={locale} />
          <MonetizationSection locale={locale} />
          <ProofBar locale={locale} />
        </main>
        <Footer locale={locale} />
      </div>
    </AuthProvider>
  );
}
